import React from 'react';
import { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Container,
  CardActionArea,
  Button,
  Divider,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';
import { useSalonsListQuery } from '../../../services/GLOWClient';
import { theme } from '../../../theme';
import { TitleScreen } from '../../../SystemDesign/styles';
import { PrimaryButton } from '../../../SystemDesign/styles';
import { BASE_URL } from '../../../config';
import { useTranslation } from 'react-i18next';
import { getLocalizedField } from '../../../utils/localizedFields';

// Helper function to build image URL - identical to the one in service.jsx
const buildImageUrl = (url) => {
  if (!url) return '';
  
  // If URL is already absolute, return as is
  if (/^https?:\/\//i.test(url)) {
    // Check if it's a localhost URL without port and add port 8012
    if (url.match(/^http:\/\/localhost\//) && !url.match(/^http:\/\/localhost:[0-9]+\//)) {
      return url.replace('http://localhost/', 'http://localhost:8012/');
    }
    return url;
  }
  
  // For relative URLs that start with /media/
  if (url.startsWith('/media/')) {
    // Use port 8012 for localhost in development
    const baseUrl = window.location.hostname === 'localhost' ? 
      'http://localhost:8012' : 
      BASE_URL.replace(/\/$/, ''); // Remove trailing slash
    return `${baseUrl}${url}`;
  }
  
  // For relative URLs that start with media/ (no leading slash)
  if (url.startsWith('media/')) {
    // Use port 8012 for localhost in development
    const baseUrl = window.location.hostname === 'localhost' ? 
      'http://localhost:8012' : 
      BASE_URL.replace(/\/$/, ''); // Remove trailing slash
    return `${baseUrl}/${url}`;
  }
  
  // For other relative URLs, prepend BASE_URL
  const baseUrl = window.location.hostname === 'localhost' ? 
    'http://localhost:8012' : 
    BASE_URL.replace(/\/$/, ''); // Remove trailing slash
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${baseUrl}${cleanUrl}`;
};

const ClientSalonList = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  // Search state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((search || '').trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [search]);

  // Query salons with backend search param
  const { data: salons, error, isLoading } = useSalonsListQuery(
    debouncedSearch ? { search: debouncedSearch, language: i18n.language } : { language: i18n.language }
  );
  
  // State to track current photo index for each salon
  const [currentPhotoIndices, setCurrentPhotoIndices] = useState({});

  const handleSalonClick = (salonId) => {
    navigate(`/salons/${salonId}`);
  };

  const handleBackToHome = () => {
    // Navigate to landing page with bypass parameter to prevent auth redirect
    window.location.replace('/?bypass=true');
  };
  
  // Function to handle previous photo navigation
  const handlePrevPhoto = (e, salonId) => {
    e.stopPropagation(); // Prevent card click
    setCurrentPhotoIndices(prev => {
      const salon = salons.find(s => s.salon_id === salonId);
      const photoCount = getSalonPhotoCount(salon);
      if (photoCount <= 1) return prev;
      
      const currentIndex = prev[salonId] || 0;
      return {
        ...prev,
        [salonId]: (currentIndex - 1 + photoCount) % photoCount
      };
    });
  };
  
  // Function to handle next photo navigation
  const handleNextPhoto = (e, salonId) => {
    e.stopPropagation(); // Prevent card click
    setCurrentPhotoIndices(prev => {
      const salon = salons.find(s => s.salon_id === salonId);
      const photoCount = getSalonPhotoCount(salon);
      if (photoCount <= 1) return prev;
      
      const currentIndex = prev[salonId] || 0;
      return {
        ...prev,
        [salonId]: (currentIndex + 1) % photoCount
      };
    });
  };
  
  // Helper function to get photo count for a salon
  const getSalonPhotoCount = (salon) => {
    if (!salon) return 0;
    
    if (salon.photos && Array.isArray(salon.photos) && salon.photos.length > 0) {
      return salon.photos.length;
    }
    
    if (salon.photo) {
      return 1;
    }
    
    return 0;
  };
  
  // Helper to find primary photo index
  const getPrimaryPhotoIndex = (salon) => {
    if (!salon || !Array.isArray(salon.photos)) return 0;
    const idx = salon.photos.findIndex(p => typeof p === 'object' && p && p.is_primary);
    return idx >= 0 ? idx : 0;
  };

  // Helper function to get current photo URL for a salon
  const getSalonPhotoUrl = (salon) => {
    if (!salon) return null;
    
    // Prefer the primary photo when no index is set yet
    const currentIndex = (salon.salon_id in currentPhotoIndices)
      ? currentPhotoIndices[salon.salon_id]
      : getPrimaryPhotoIndex(salon);
    
    // Case 1: Check photos array
    if (salon.photos && Array.isArray(salon.photos) && salon.photos.length > 0) {
      const photo = salon.photos[currentIndex];
      
      if (typeof photo === 'string') {
        return buildImageUrl(photo);
      } else if (photo && photo.url) {
        return buildImageUrl(photo.url);
      }
    }
    
    // Case 2: Check if there's a direct photo property as fallback
    if (salon.photo) {
      return buildImageUrl(salon.photo);
    }
    
    // Default placeholder
    return 'https://via.placeholder.com/400x240?text=Salon+Image';
  };
  
  // Initialize photo indices when salons data is loaded, preferring primary
  useEffect(() => {
    if (salons && salons.length > 0) {
      const initialIndices = {};
      salons.forEach(salon => {
        initialIndices[salon.salon_id] = getPrimaryPhotoIndex(salon);
      });
      setCurrentPhotoIndices(initialIndices);
    }
  }, [salons]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: theme.colors.primary[500] }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          {t('client_salon_list.error_loading', { error: error.message || t('common.unknown_error') })}
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ 
        textAlign: 'center', 
        mb: 6,
        mt: 4 
      }}>
        {/* Back to Home Button */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-start', 
          mb: 3,
          maxWidth: '1200px',
          mx: 'auto'
        }}>
          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={handleBackToHome}
            sx={{
              color: theme.colors.primary[600],
              borderColor: theme.colors.primary[300],
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              borderRadius: '25px',
              px: 3,
              py: 1,
              '&:hover': {
                backgroundColor: theme.colors.primary[50],
                borderColor: theme.colors.primary[400]
              }
            }}
          >
            {t('client_salon_list.back_to_home')}
          </Button>
        </Box>

        <Typography 
          variant="h3" 
          sx={{ 
            ...TitleScreen(), 
            color: theme.colors.primary[700],
            fontWeight: 800,
            mb: 2,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            letterSpacing: '-0.02em'
          }}
        >
          {t('client_salon_list.title')}
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            color: theme.colors.primary[500],
            fontWeight: 400,
            opacity: 0.8
          }}
        >
          {t('client_salon_list.subtitle')}
        </Typography>
        {/* Search bar */}
        <Box sx={{ mt: 4, maxWidth: 600, mx: 'auto' }}>
          <TextField
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('client_salon_list.search_placeholder')}
            variant="outlined"
            size="medium"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              )
            }}
          />
        </Box>
      </Box>
      
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        mt: 4 
      }}>
        {/* No results state */}
        {Array.isArray(salons) && salons.length === 0 && debouncedSearch && (
          <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 6 }}>
            <Typography variant="body1">
              {t('client_salon_list.no_results', { search })}
            </Typography>
          </Box>
        )}
        {salons && salons.map((salon) => (
          <Card
            key={salon.salon_id}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              width: '100%',
              maxWidth: '900px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: `1px solid ${theme.colors.secondary[100]}`,
              backgroundColor: '#ffffff',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                borderColor: theme.colors.primary[200]
              },
              cursor: 'pointer',
            }}
            onClick={() => handleSalonClick(salon.salon_id)}
          >
            <Box sx={{
              position: 'relative',
              width: { xs: '100%', md: '40%' },
              height: { xs: 220, md: 240 },
            }}>
              <CardMedia
                component="img"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.3s ease'
                }}
                image={getSalonPhotoUrl(salon)}
                alt={getLocalizedField(salon, 'name', i18n.language)}
              />
              
              {/* Photo navigation arrows - only show if multiple photos */}
              {getSalonPhotoCount(salon) > 1 && (
                <>
                  <IconButton 
                    onClick={(e) => handlePrevPhoto(e, salon.salon_id)}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.7)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                      width: 30,
                      height: 30,
                      '& .MuiSvgIcon-root': { fontSize: 18 }
                    }}
                  >
                    <ArrowBackIosNewIcon />
                  </IconButton>
                  
                  <IconButton 
                    onClick={(e) => handleNextPhoto(e, salon.salon_id)}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.7)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                      width: 30,
                      height: 30,
                      '& .MuiSvgIcon-root': { fontSize: 18 }
                    }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                  
                  {/* Photo counter */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem'
                    }}
                  >
                    {(currentPhotoIndices[salon.salon_id] || 0) + 1}/{getSalonPhotoCount(salon)}
                  </Box>
                </>
              )}
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              flex: 1,
              p: { xs: 3, md: 4 },
              justifyContent: 'space-between'
            }}>
              <Box>
      
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography 
                  variant="h4" 
                  component="h2" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                    lineHeight: 1.2,
                    mb: 1,
                    color: theme.colors.primary[700]
                  }}
                >
                  {getLocalizedField(salon, 'name', i18n.language)}
                </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'text.secondary',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.95rem',
                      mb: 0.5
                    }}
                  >
                    <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                      📍
                    </Box>
                    {salon.address}
                  </Typography>
                </Box>
                    
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                     {salon.description && (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 3,
                      lineHeight: 1.7,
                      fontSize: '0.95rem',
                      color: 'text.secondary'
                    }}
                  >
                    {salon.description.length > 150
                      ? `${salon.description.substring(0, 150)}...`
                      : salon.description}
                  </Typography>
                )}
                </Box>
                
             
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                mt: 'auto'
              }}>
                <Button
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSalonClick(salon.salon_id);
                  }}
                  sx={{
                    ...PrimaryButton,
                    backgroundColor: theme.colors.primary[500],
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: 600,
                    padding: '12px 28px',
                    borderRadius: '30px',
                    textTransform: 'none',
                    minWidth: '140px',
                    boxShadow: '0 4px 16px rgba(226, 194, 156, 0.25)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: theme.colors.primary[400],
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(226, 194, 156, 0.35)'
                    }
                  }}
                >
                  {t('client_salon_list.view_details')}
                </Button>
              </Box>
            </Box>
          </Card>
        ))}
      </Box>
    </Container>
  );
};

export default ClientSalonList;
